import { Component } from '@theme/component';

class TrendingCarousel extends Component {
  requiredRefs = ['swiper', 'modalContainer'];

  #swiper = null;
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    
    this.#copyModalContent();
    
    this.#initSwiper();
  }

  #initSwiper() {
    if (! this.refs.swiper) {
      return;
    }

    this.#swiper = new window.Swiper(this.refs.swiper, {
      loop: true,
      allowTouchMove: true,
      slidesPerView: 1.2,
      spaceBetween: 20,
      
      breakpoints: {
        750: {
          slidesPerView: 3,
          centeredSlides: true,
        }
      }
    });
  }

  /**
   * Copy the modal HTML out of the blocks to issues with display settings.
   */
  #copyModalContent() {
    const copiedIds = [];
    const modals = this.querySelectorAll('.trending-carousel-item .trending-carousel-item__modal-content');

    modals.forEach((modal) => {
      // Use the block ID to ensure that for duplicated slides the modal is only copied once.
      if (copiedIds.includes(modal.dataset.blockId)) {
        return;
      }

      this.refs.modalContainer.insertAdjacentHTML('beforeend', modal.outerHTML);
      
      copiedIds.push(modal.dataset.blockId);
    });
  }
  
  slideNext(){
    this.#swiper.slideNext();
  }

  slidePrev() {
    this.#swiper.slidePrev();
  }

  /**
   * Show the modal.
   * 
   * @param {PointerEvent} event 
   */
  showModal(event) {
    const button = event.target;
    const modal = this.refs.modalContainer.querySelector(`[data-block-id="${button.dataset.blockId}"]`);
    modal.classList.add('trending-carousel-item__modal-content--open');
    this.pauseVideos();
    this.#lockBodyScroll();
  }

  closeModal() {
    const modal = this.refs.modalContainer.querySelector('.trending-carousel-item__modal-content--open');
    modal?.classList.remove('trending-carousel-item__modal-content--open');
    modal?.querySelector('video')?.pause();

    this.#unlockBodyScroll();
  }

  showNextModal() {
    const currentModal = this.refs.modalContainer.querySelector('.trending-carousel-item__modal-content--open');
    const nextModal = currentModal.nextElementSibling || this.refs.modalContainer.firstElementChild;
    
    this.#switchVisibleModal(currentModal, nextModal);
  }

  showPrevModal() {
    const currentModal = this.refs.modalContainer.querySelector('.trending-carousel-item__modal-content--open');
    const prevModal = currentModal.previousElementSibling || this.refs.modalContainer.lastElementChild;
    
    this.#switchVisibleModal(currentModal, prevModal);
  }

  toggleVideoPlay(event) {
    const parent = event.target.parentNode;
    const video = parent.querySelector('video');

    if (video.paused) {
      video.loop = true;
      video.muted = false;
      video.play();

      this.#togglePlayIcons(event.target, true);
      this.pauseVideos(video);
    } else {
      video.pause();

      this.#togglePlayIcons(event.target, false);
    }
  }


  /**
   * Loop through all of the video elements in the carousel and pause anything 
   * that should not be currently playing.
   * 
   * @param {HTMLVideoElement | null} excludedVideo The video that is currently playing.
   */
  pauseVideos(excludedVideo = null) {
    const trendingCarousel = this.closest('trending-carousel');
    const allVideos = trendingCarousel?.querySelectorAll('video');

    if (! allVideos) {
      return;
    }

    [...allVideos]
      ?.filter((video) => !excludedVideo || (!video.isSameNode(excludedVideo) && !video.paused))
      .forEach((video) => {
        video.pause();
        
        const button = video.parentNode?.querySelector('.trending-carousel-item__play');
        this.#togglePlayIcons(button, false);
      })
  }

  #togglePlayIcons(parent, isPlaying) {
    const playIcon = parent.querySelector('.icon-play-button__play');
    const pauseIcon = parent.querySelector('.icon-play-button__pause');

     playIcon.classList.toggle('icon-play-button--hidden', isPlaying);
    pauseIcon.classList.toggle('icon-play-button--hidden', !isPlaying);
  }

  #switchVisibleModal(currentModal, nextModal) {
    currentModal.classList.remove('trending-carousel-item__modal-content--open');
    currentModal.querySelector('video')?.pause();
    nextModal.classList.add('trending-carousel-item__modal-content--open');
  }

  #lockBodyScroll() {
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    });
  }

  #unlockBodyScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }
}

if (!customElements.get('trending-carousel')) {
  customElements.define('trending-carousel', TrendingCarousel);
}